import type { User } from '../types/auth';
import { createLogger } from './logger';

const logger = createLogger({ module: 'StatsigUser', level: 'info' });

export interface StatsigUser {
  userID: string;
  email?: string;
  custom?: {
    user_type?: string;
    signup_date?: string;
    activity_level?: string;
    device_type?: string;
    subscription_tier?: string;
    feature_beta_participant?: boolean;
    geographic_region?: string;
    browser?: string;
    platform?: string;
    onboarding_complete?: boolean;
    mfa_enabled?: boolean;
    account_age_days?: number;
    last_active?: string;
    timezone?: string;
    company?: string;
    job_title?: string;
    career_stage?: 'entry' | 'mid' | 'senior' | 'executive';
    session_type?: string;
  };
  privateAttributes?: {
    internal_user_id?: string;
    created_at?: string;
  };
}

function getDeviceType(): string {
  if (typeof window === 'undefined') return 'server';
  
  const width = window.innerWidth;
  if (width < 768) return 'mobile';
  if (width < 1024) return 'tablet';
  return 'desktop';
}

function getGeographicRegion(): string {
  if (typeof window === 'undefined') return 'unknown';
  
  // Use Intl.DateTimeFormat to get timezone-based region
  try {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (timezone.includes('America')) return 'americas';
    if (timezone.includes('Europe')) return 'europe';
    if (timezone.includes('Asia')) return 'asia';
    if (timezone.includes('Australia')) return 'oceania';
    return 'other';
  } catch {
    return 'unknown';
  }
}

function getBrowserInfo(): { browser: string; platform: string } {
  if (typeof navigator === 'undefined') {
    return { browser: 'unknown', platform: 'unknown' };
  }

  const userAgent = navigator.userAgent;
  
  // Basic browser detection
  let browser = 'unknown';
  if (userAgent.includes('Chrome')) browser = 'chrome';
  else if (userAgent.includes('Firefox')) browser = 'firefox';
  else if (userAgent.includes('Safari')) browser = 'safari';
  else if (userAgent.includes('Edge')) browser = 'edge';

  // Basic platform detection
  let platform = 'unknown';
  if (userAgent.includes('Windows')) platform = 'windows';
  else if (userAgent.includes('Mac')) platform = 'macos';
  else if (userAgent.includes('Linux')) platform = 'linux';
  else if (userAgent.includes('Android')) platform = 'android';
  else if (userAgent.includes('iOS')) platform = 'ios';

  return { browser, platform };
}

function calculateActivityLevel(user: User): string {
  // Implement your activity level calculation logic
  // This is a placeholder implementation
  const signupDate = user.created_at ? new Date(user.created_at) : new Date();
  const daysSinceSignup = (Date.now() - signupDate.getTime()) / (1000 * 60 * 60 * 24);
  
  if (daysSinceSignup < 7) return 'new';
  if (daysSinceSignup < 30) return 'active';
  if (daysSinceSignup < 90) return 'regular';
  return 'veteran';
}

function calculateAccountAge(user: User): number {
  const signupDate = user.created_at ? new Date(user.created_at) : new Date();
  return Math.floor((Date.now() - signupDate.getTime()) / (1000 * 60 * 60 * 24));
}

export function mapSupabaseUserToStatsig(
  user: User | null,
  userProfile?: Record<string, unknown>,
  additionalProperties?: Record<string, unknown>
): StatsigUser {
  // Add debugging to help track when anonymous users are created
  if (!user) {
    logger.debug('Creating anonymous user (no Supabase user provided)');
    const { browser, platform } = getBrowserInfo();
    return {
      userID: 'anonymous',
      custom: {
        device_type: getDeviceType(),
        geographic_region: getGeographicRegion(),
        browser,
        platform,
        session_type: 'anonymous',
        ...additionalProperties
      }
    };
  }

  // We have a real user - log this for debugging
  logger.debug('Creating authenticated user', { 
    userId: user.id.substring(0, 8) + '...', 
    email: user.email?.substring(0, 3) + '***' 
  });

  const { browser, platform } = getBrowserInfo();
  const accountAgeDays = calculateAccountAge(user);

  return {
    userID: user.id,
    email: user.email,
    custom: {
      user_type: (userProfile?.user_type as string) || 'standard', // Now getting user_type from database via userProfile
      signup_date: user.created_at || new Date().toISOString(),
      device_type: getDeviceType(),
      subscription_tier: (userProfile?.subscription_tier as string) || (user.user_metadata?.subscription_tier as string) || 'free',
      feature_beta_participant: (userProfile?.beta_participant as boolean) || (user.user_metadata?.beta_participant as boolean) || false,
      activity_level: calculateActivityLevel(user),
      geographic_region: getGeographicRegion(),
      browser,
      platform,
      onboarding_complete: (userProfile?.onboarding_complete as boolean) || (user.user_metadata?.onboarding_complete as boolean) || false,
      mfa_enabled: (user.user_metadata?.mfa_enabled as boolean) || false,
      account_age_days: accountAgeDays,
      last_active: user.updated_at || user.created_at || new Date().toISOString(),
      timezone: (userProfile?.timezone as string) || Intl.DateTimeFormat().resolvedOptions().timeZone,
      company: (userProfile?.company as string | undefined) || (user.user_metadata?.company as string | undefined),
      job_title: (userProfile?.job_title as string | undefined) || (user.user_metadata?.job_title as string | undefined),
      career_stage: (userProfile?.career_stage as 'entry' | 'mid' | 'senior' | 'executive') || (user.user_metadata?.career_stage as 'entry' | 'mid' | 'senior' | 'executive') || 'entry',
      session_type: 'authenticated',
      ...additionalProperties
    },
    privateAttributes: {
      internal_user_id: user.id,
      created_at: user.created_at || new Date().toISOString(),
    }
  };
} 